import {
  License,
  SessionType,
  CurrentCycleDataResponse,
  PaginationParams,
  getURLParams,
  LicenseStatus,
  CycleConfig,
  CycleLogActivityResponse,
  NodesByCycleIdResponse,
  SessionLogInCycleByNodeIdResponse,
  ClaimableByLicenseResponse,
  RewardActivityResponse,
  Proposal,
  ProposalListResponse,
  ProposalDetailResponse,
  ProposalTally,
  ProposalCategory,
  ProposalStatus,
  ProposalExecutionType,
  VoteChoice,
  DaoEligibilityResult
} from '@mprolab/sdk'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { withSessionRecovery } from 'state/services/sessionRecovery'
import getApiHeaders from 'utils/getApiHeaders'

interface GuardParams {
  account: string
  accessToken: string
}

export interface SignatureResponse {
  hash: string
}

export interface SignatureParams {
  account: string
}

export interface LoginResponse {
  access_token: string
}

export interface LoginParams {
  wallet: string
  signature: string
  account: string
  hash: string
  sessionType: SessionType
}

export interface LicenseParams extends GuardParams {
  licenseId?: string
}

export interface UpdateLicense extends LicenseParams {
  name: string
}

export interface UpdateLicenseStatus extends LicenseParams {
  status: LicenseStatus
}

interface JoinStakeParams extends GuardParams {
  stakeContractAddress: string
}

export enum LicenseApiTag {
  LICENSE = 'LICENSE',
  NODE_STORAGE = 'NODE_STORAGE',
  PROFILE = 'PROFILE',
  PROPOSAL = 'PROPOSAL'
}

/** A person, as the platform knows them. Every field optional but the switch. */
export interface UserProfile {
  wallet: string
  name?: string | null
  tag?: string | null
  bio?: string | null
  avatarPath?: string | null
  linkX?: string | null
  linkLinkedIn?: string | null
  linkFacebook?: string | null
  isPublic: boolean
}

/** What a stranger sees. No `isPublic`: a card only exists when it is public. */
export type PublicProfileCard = Omit<UserProfile, 'isPublic'>

export interface NodeStorageSummary {
  licenseId: string
  licenseType: string
  quotaBytes: number
  usedBytes: number
  volumeRelativePath: string
  chunkUpload?: {
    maxChunkBytes: number
    recommendedChunkBytes: number
  }
}

export interface NodeStorageListEntry {
  name: string
  isDirectory: boolean
  size: number | null
  mtimeMs: number
}

export interface NodeStorageListResponse {
  path: string
  entries: NodeStorageListEntry[]
}

export interface NodeStorageUploadResult {
  path: string
  size: number
  usedBytes: number
}

/** Header value must match `microservices/index-node-licenses` `NODE_STORAGE_SHARE_TOKEN_HEADER`. */
export const NODE_STORAGE_SHARE_TOKEN_HEADER = 'X-Node-Storage-Share-Token'

export type NodeStorageSharePermission = 'read' | 'read_write'

export interface NodeStorageShareRow {
  shareId: string
  folderPath: string
  permission: NodeStorageSharePermission
  createdAt: string
  revokedAt: string | null
}

export interface NodeStorageShareCreated {
  shareId: string
  token: string
  folderPath: string
  permission: NodeStorageSharePermission
}

export interface PublicNodeStorageShareSummary {
  permission: NodeStorageSharePermission
  folderPath: string
  chunkUpload?: {
    maxChunkBytes: number
    recommendedChunkBytes: number
  }
}

/**
 * The user service — people, as opposed to licences. Same shape as the
 * licences base url so a testnet or a fork overrides it the same way.
 */
export const userServiceBaseUrl = (() => {
  const raw = import.meta.env.VITE_USER_SERVICE_API_URL ?? '/api/user-service/'
  return raw.endsWith('/') ? raw : `${raw}/`
})()

export const indexNodeLicensesBaseUrl = (() => {
  const raw = import.meta.env.VITE_INDEX_NODE_SERVICE_API_URL ?? '/api/index-node-licenses/'
  return raw.endsWith('/') ? raw : `${raw}/`
})()

type CycleLogActivitiesParams = PaginationParams & GuardParams
interface NodesPerCycleLogParams extends GuardParams {
  cycleId: string
}
interface SessionsPerNodeLogParams extends GuardParams, PaginationParams {
  cycleId: string
  licenseId: string
}

export const indexNodeApi = createApi({
  reducerPath: 'indexNodeApi',
  baseQuery: withSessionRecovery(
    fetchBaseQuery({
      baseUrl: indexNodeLicensesBaseUrl
    })
  ),
  tagTypes: [
    LicenseApiTag.LICENSE,
    LicenseApiTag.NODE_STORAGE,
    LicenseApiTag.PROFILE,
    LicenseApiTag.PROPOSAL
  ],
  endpoints: (builder) => ({
    getSignatureHash: builder.query<SignatureResponse, SignatureParams>({
      query: (params) => {
        const { account } = params
        return {
          url: `/auth/signature/hash`,
          method: 'GET',
          headers: getApiHeaders({ account })
        }
      }
    }),
    login: builder.mutation<LoginResponse, LoginParams>({
      query: (params) => {
        const { signature, account, hash, sessionType, wallet } = params
        return {
          url: `/auth/login`,
          method: 'POST',
          headers: getApiHeaders({ account, hash, sessionType }),
          body: JSON.stringify({ wallet, signature })
        }
      }
    }),
    /* Deliberately unauthenticated: this is the card strangers see. */
    /*
     * Names for a batch of wallets, so a card can show a person instead of an
     * address. A wallet with a private profile is absent from the answer, not
     * present with nulls — see the service for why that difference matters.
     */
    getLicenses: builder.query<License[], LicenseParams>({
      query: (params: LicenseParams) => {
        const { account, accessToken } = params
        return {
          url: `licenses/owner/${account}`,
          method: 'GET',
          headers: getApiHeaders({ account, accessToken })
        }
      },
      providesTags: [LicenseApiTag.LICENSE]
    }),
    syncLicenses: builder.mutation<License[], LicenseParams>({
      query: (params: LicenseParams) => {
        const { account, accessToken } = params
        return {
          url: 'licenses/sync',
          method: 'POST',
          headers: getApiHeaders({ account, accessToken })
        }
      },
      invalidatesTags: [LicenseApiTag.LICENSE]
    }),
    getLicenseKey: builder.query<{ licenseKey: string }, LicenseParams>({
      query: (params: LicenseParams) => {
        const { account, accessToken, licenseId } = params
        return {
          url: `licenses/${licenseId}/key`,
          method: 'GET',
          headers: getApiHeaders({ account, accessToken })
        }
      },
      providesTags: [LicenseApiTag.LICENSE]
    }),
    /**
     * Detach a licence from the Terminal holding it.
     *
     * The endpoint has existed since the binding did; nothing in the portal
     * called it, so a holder who changed machines had a licence stuck to a
     * computer they might no longer own. It is the owner's act and nobody
     * else's — the licences service checks the wallet against the licence
     * before it unbinds anything.
     *
     * Invalidating LICENSE is what makes the row's dot go grey: the list is
     * cached for a minute upstream and this is the one moment that staleness
     * would be visible as a wrong answer rather than a slightly old one.
     */
    releaseTerminal: builder.mutation<{ licenseId: string; released: boolean }, LicenseParams>({
      query: (params: LicenseParams) => {
        const { account, accessToken, licenseId } = params
        return {
          url: 'terminal/release',
          method: 'POST',
          headers: { ...getApiHeaders({ account, accessToken }), 'Content-Type': 'application/json' },
          body: JSON.stringify({ licenseId })
        }
      },
      invalidatesTags: [LicenseApiTag.LICENSE]
    }),
    updateLicenseName: builder.mutation<License, UpdateLicense>({
      query: (params: UpdateLicense) => {
        const { account, accessToken, licenseId, name } = params
        return {
          url: `licenses/${licenseId}/name`,
          method: 'PATCH',
          headers: getApiHeaders({ account, accessToken }),
          body: JSON.stringify({ name })
        }
      }
    }),
    updateLicenseStatus: builder.mutation<License, UpdateLicenseStatus>({
      query: (params: UpdateLicenseStatus) => {
        const { account, accessToken, status, licenseId } = params
        return {
          url: `licenses/${licenseId}/status`,
          method: 'PATCH',
          headers: getApiHeaders({ account, accessToken }),
          body: { status }
        }
      },
      invalidatesTags: [LicenseApiTag.LICENSE]
    }),
    getCurrentCycleNodesData: builder.query<CurrentCycleDataResponse[], LicenseParams>({
      query: (params: LicenseParams) => {
        const { account, accessToken } = params
        return {
          url: 'licenses/current-cycle/data',
          method: 'GET',
          headers: getApiHeaders({ account, accessToken })
        }
      }
    }),
    getCycleConfig: builder.query<CycleConfig, undefined>({
      query: () => {
        return {
          url: 'cycle-config',
          method: 'GET'
        }
      }
    }),
    joinStake: builder.mutation<License, JoinStakeParams>({
      query: (params: JoinStakeParams) => {
        const { account, accessToken, stakeContractAddress } = params
        return {
          url: `licenses/stake/${stakeContractAddress}`,
          method: 'PATCH',
          headers: getApiHeaders({ account, accessToken })
        }
      },
      invalidatesTags: [LicenseApiTag.LICENSE]
    }),
    getCycleLogActivites: builder.query<CycleLogActivityResponse, CycleLogActivitiesParams>({
      query: (params: CycleLogActivitiesParams) => {
        const { account, accessToken } = params
        const requestParams = getURLParams({ params, paramsToSkip: ['account', 'accessToken'] })
        return {
          url: 'license-cycle-data/cycle-log',
          method: 'GET',
          headers: getApiHeaders({ account, accessToken }),
          params: requestParams
        }
      }
    }),
    /**
     * Per-license unclaimed reward breakdown — used by the Rewards page to
     * show base + booster split per license. Backed by license-bound
     * filter `licenseId IN ownedByWallet` on the backend (ADR 0002).
     */
    getClaimableByLicense: builder.query<ClaimableByLicenseResponse, GuardParams>({
      query: ({ account, accessToken }) => ({
        url: 'license-cycle-data/claimable-by-license',
        method: 'GET',
        headers: getApiHeaders({ account, accessToken })
      })
    }),
    /**
     * One activity log for the wallet: what is earned and not yet minted, and
     * every mint with the licences behind it. Claims are read from the contract
     * by the client — the proxy emits no event on claim, so a server-side claim
     * list would be invented rather than observed.
     */
    getRewardActivity: builder.query<RewardActivityResponse, GuardParams>({
      query: ({ account, accessToken }) => ({
        url: 'license-cycle-data/activity',
        method: 'GET',
        headers: getApiHeaders({ account, accessToken })
      })
    }),
    getNodesPerCycleLog: builder.query<NodesByCycleIdResponse, NodesPerCycleLogParams>({
      query: (params: NodesPerCycleLogParams) => {
        const { account, accessToken, cycleId } = params
        const requestParams = getURLParams({ params, paramsToSkip: ['account', 'accessToken', 'cycleId'] })
        return {
          url: `license-cycle-data/${cycleId}/nodes`,
          method: 'GET',
          headers: getApiHeaders({ account, accessToken }),
          params: requestParams
        }
      }
    }),
    getSessionsPerNodeLog: builder.query<SessionLogInCycleByNodeIdResponse, SessionsPerNodeLogParams>({
      query: (params: SessionsPerNodeLogParams) => {
        const { account, accessToken, cycleId, licenseId } = params
        const requestParams = getURLParams({ params, paramsToSkip: ['account', 'accessToken', 'licenseId', 'cycleId'] })
        return {
          url: `session-logs/license/${licenseId}/cycle/${cycleId}`,
          method: 'GET',
          headers: getApiHeaders({ account, accessToken }),
          params: requestParams
        }
      }
    }),
    getLicensesInNetwork: builder.query<
      {
        count: number
        type: string
      }[],
      LicenseParams
    >({
      query: (params: LicenseParams) => {
        const { account, accessToken } = params
        return {
          url: 'licenses/count',
          method: 'GET',
          headers: getApiHeaders({ account, accessToken })
        }
      }
    }),
    getAvailableStakes: builder.query<{ availableStakes: string[] }, LicenseParams>({
      query: (params: LicenseParams) => {
        const { account, accessToken } = params
        return {
          url: 'licenses/available-stakes',
          method: 'GET',
          headers: getApiHeaders({ account, accessToken })
        }
      }
    }),
    getNodeStorageSummary: builder.query<
      NodeStorageSummary,
      { account: string; accessToken: string; licenseId: string }
    >({
      query: ({ account, accessToken, licenseId }) => ({
        url: `licenses/${licenseId}/storage`,
        method: 'GET',
        headers: getApiHeaders({ account, accessToken })
      }),
      providesTags: (_r, _e, arg) => [{ type: LicenseApiTag.NODE_STORAGE, id: arg.licenseId }]
    }),
    getNodeStorageList: builder.query<
      NodeStorageListResponse,
      { account: string; accessToken: string; licenseId: string; path?: string }
    >({
      query: ({ account, accessToken, licenseId, path: folderPath }) => ({
        url: `licenses/${licenseId}/storage/list`,
        method: 'GET',
        headers: getApiHeaders({ account, accessToken }),
        params: folderPath ? { path: folderPath } : {}
      }),
      providesTags: (_r, _e, arg) => [{ type: LicenseApiTag.NODE_STORAGE, id: arg.licenseId }]
    }),
    uploadNodeStorageFile: builder.mutation<
      NodeStorageUploadResult,
      { account: string; accessToken: string; licenseId: string; file: File; relativePath?: string }
    >({
      async queryFn(arg) {
        const form = new FormData()
        form.append('file', arg.file)
        if (arg.relativePath) {
          form.append('relativePath', arg.relativePath)
        }
        const headers = getApiHeaders({ account: arg.account, accessToken: arg.accessToken })
        delete (headers as Record<string, string>)['Content-Type']
        const base = indexNodeLicensesBaseUrl.replace(/\/$/, '')
        const res = await fetch(`${base}/licenses/${arg.licenseId}/storage/upload`, {
          method: 'POST',
          headers: headers as HeadersInit,
          body: form
        })
        if (!res.ok) {
          const text = await res.text()
          return { error: { status: res.status, data: text } as const }
        }
        const data = (await res.json()) as NodeStorageUploadResult
        return { data }
      },
      invalidatesTags: (_r, _e, arg) => [{ type: LicenseApiTag.NODE_STORAGE, id: arg.licenseId }]
    }),
    deleteNodeStorageFile: builder.mutation<
      void,
      { account: string; accessToken: string; licenseId: string; path: string }
    >({
      query: ({ account, accessToken, licenseId, path: filePath }) => ({
        url: `licenses/${licenseId}/storage/file`,
        method: 'DELETE',
        headers: getApiHeaders({ account, accessToken }),
        params: { path: filePath }
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: LicenseApiTag.NODE_STORAGE, id: arg.licenseId }]
    }),
    mkdirNodeStorageFolder: builder.mutation<
      { path: string },
      { account: string; accessToken: string; licenseId: string; path: string }
    >({
      query: ({ account, accessToken, licenseId, path: folderPath }) => ({
        url: `licenses/${licenseId}/storage/mkdir`,
        method: 'POST',
        headers: getApiHeaders({ account, accessToken }),
        params: { path: folderPath }
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: LicenseApiTag.NODE_STORAGE, id: arg.licenseId }]
    }),
    moveNodeStorageItem: builder.mutation<
      { ok: boolean; from: string; to: string },
      { account: string; accessToken: string; licenseId: string; from: string; to: string }
    >({
      query: ({ account, accessToken, licenseId, from, to }) => ({
        url: `licenses/${licenseId}/storage/move`,
        method: 'POST',
        headers: getApiHeaders({ account, accessToken }),
        params: { from, to }
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: LicenseApiTag.NODE_STORAGE, id: arg.licenseId }]
    }),
    getNodeStorageShares: builder.query<
      NodeStorageShareRow[],
      { account: string; accessToken: string; licenseId: string }
    >({
      query: ({ account, accessToken, licenseId }) => ({
        url: `licenses/${licenseId}/storage/shares`,
        method: 'GET',
        headers: getApiHeaders({ account, accessToken })
      }),
      providesTags: (_r, _e, arg) => [{ type: LicenseApiTag.NODE_STORAGE, id: arg.licenseId }]
    }),
    createNodeStorageShare: builder.mutation<
      NodeStorageShareCreated,
      {
        account: string
        accessToken: string
        licenseId: string
        folderPath: string
        permission: NodeStorageSharePermission
      }
    >({
      query: ({ account, accessToken, licenseId, folderPath, permission }) => ({
        url: `licenses/${licenseId}/storage/shares`,
        method: 'POST',
        headers: {
          ...getApiHeaders({ account, accessToken }),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ folderPath, permission })
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: LicenseApiTag.NODE_STORAGE, id: arg.licenseId }]
    }),
    updateNodeStorageShare: builder.mutation<
      { ok: boolean },
      {
        account: string
        accessToken: string
        licenseId: string
        shareId: string
        permission: NodeStorageSharePermission
      }
    >({
      query: ({ account, accessToken, licenseId, shareId, permission }) => ({
        url: `licenses/${licenseId}/storage/shares/${shareId}`,
        method: 'PATCH',
        headers: {
          ...getApiHeaders({ account, accessToken }),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ permission })
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: LicenseApiTag.NODE_STORAGE, id: arg.licenseId }]
    }),
    rotateNodeStorageShareToken: builder.mutation<
      { token: string },
      { account: string; accessToken: string; licenseId: string; shareId: string }
    >({
      query: ({ account, accessToken, licenseId, shareId }) => ({
        url: `licenses/${licenseId}/storage/shares/${shareId}/rotate-token`,
        method: 'POST',
        headers: getApiHeaders({ account, accessToken })
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: LicenseApiTag.NODE_STORAGE, id: arg.licenseId }]
    }),
    revokeNodeStorageShare: builder.mutation<
      { ok: boolean },
      { account: string; accessToken: string; licenseId: string; shareId: string }
    >({
      query: ({ account, accessToken, licenseId, shareId }) => ({
        url: `licenses/${licenseId}/storage/shares/${shareId}`,
        method: 'DELETE',
        headers: getApiHeaders({ account, accessToken })
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: LicenseApiTag.NODE_STORAGE, id: arg.licenseId }]
    }),
    getPublicNodeStorageShareSummary: builder.query<PublicNodeStorageShareSummary, { token: string }>({
      query: ({ token }) => ({
        url: 'public/node-storage/summary',
        method: 'GET',
        headers: { [NODE_STORAGE_SHARE_TOKEN_HEADER]: token }
      })
    }),
    getPublicNodeStorageShareList: builder.query<NodeStorageListResponse, { token: string; path?: string }>({
      query: ({ token, path: p }) => ({
        url: 'public/node-storage/list',
        method: 'GET',
        headers: { [NODE_STORAGE_SHARE_TOKEN_HEADER]: token },
        params: p ? { path: p } : {}
      })
    }),
    // ----------------------------------------------------------------
    // Phase 4 — Network DAO MVP. See ADR 0002 / PHASE-4-DAO-MVP-PLAN.md.
    // Public reads (proposals list / detail / tally) need no wallet auth;
    // create + vote + eligibility require the existing wallet JWT.
    // ----------------------------------------------------------------
    getProposals: builder.query<
      ProposalListResponse,
      {
        status?: ProposalStatus
        category?: ProposalCategory
        skip?: number
        limit?: number
      }
    >({
      query: (params) => ({
        url: 'dao/proposals',
        method: 'GET',
        params: getURLParams({ params, paramsToSkip: [] })
      })
    }),
    /**
     * A proposal, and — when a session is passed — how this wallet voted.
     *
     * The session is the whole point. The service returns `myVote` only when it
     * can see who is asking, and this request used to send no wallet at all: so
     * `myVote` was always absent, the vote buttons were always offered, and
     * pressing one a second time produced a 409 the voter read as a fault. The
     * ballot had been counted and the screen could not say so.
     */
    getProposal: builder.query<
      ProposalDetailResponse,
      { proposalId: string; account?: string; accessToken?: string }
    >({
      query: ({ proposalId, account, accessToken }) => ({
        url: `dao/proposals/${proposalId}`,
        method: 'GET',
        headers:
          account && accessToken ? getApiHeaders({ account, accessToken }) : undefined
      }),
      providesTags: (_r, _e, arg) => [
        { type: LicenseApiTag.PROPOSAL, id: arg.proposalId }
      ]
    }),
    getProposalTally: builder.query<ProposalTally, { proposalId: string }>({
      query: ({ proposalId }) => ({
        url: `dao/proposals/${proposalId}/tally`,
        method: 'GET'
      })
    }),
    getDaoEligibility: builder.query<DaoEligibilityResult, GuardParams>({
      query: ({ account, accessToken }) => ({
        url: 'dao/eligibility',
        method: 'GET',
        headers: getApiHeaders({ account, accessToken })
      })
    }),
    createProposal: builder.mutation<
      Proposal,
      GuardParams & {
        title: string
        bodyMarkdown: string
        category: ProposalCategory
        executionType: ProposalExecutionType
      }
    >({
      query: ({ account, accessToken, ...body }) => ({
        url: 'dao/proposals',
        method: 'POST',
        headers: {
          ...getApiHeaders({ account, accessToken }),
          'Content-Type': 'application/json'
        },
        body
      })
    }),
    castVote: builder.mutation<
      { ok: true; tally: ProposalTally },
      GuardParams & {
        proposalId: string
        choice: VoteChoice
        signature: string
        signedMessageVersion: string
        signedDigest: string
        castAt: number
      }
    >({
      query: ({ account, accessToken, proposalId, ...body }) => ({
        url: `dao/proposals/${proposalId}/vote`,
        method: 'POST',
        headers: {
          ...getApiHeaders({ account, accessToken }),
          'Content-Type': 'application/json'
        },
        body
      }),
      // Without this the screen keeps the pre-vote copy of the proposal — and
      // therefore keeps offering the buttons — until the 30-second poll.
      invalidatesTags: (_r, _e, arg) => [
        { type: LicenseApiTag.PROPOSAL, id: arg.proposalId }
      ]
    })
  })
})

export const {
  useGetSignatureHashQuery,
  useLazyGetSignatureHashQuery,
  useLoginMutation,
  useGetLicensesQuery,
  useLazyGetLicensesQuery,
  useGetLicenseKeyQuery,
  useLazyGetLicenseKeyQuery,
  useReleaseTerminalMutation,
  useUpdateLicenseNameMutation,
  useUpdateLicenseStatusMutation,
  useSyncLicensesMutation,
  useGetCurrentCycleNodesDataQuery,
  useLazyGetCurrentCycleNodesDataQuery,
  useGetCycleConfigQuery,
  useLazyGetCycleConfigQuery,
  useJoinStakeMutation,
  useGetCycleLogActivitesQuery,
  useLazyGetCycleLogActivitesQuery,
  useGetClaimableByLicenseQuery,
  useGetRewardActivityQuery,
  useLazyGetClaimableByLicenseQuery,
  useGetNodesPerCycleLogQuery,
  useLazyGetNodesPerCycleLogQuery,
  useGetSessionsPerNodeLogQuery,
  useLazyGetSessionsPerNodeLogQuery,
  useGetLicensesInNetworkQuery,
  useGetAvailableStakesQuery,
  useGetNodeStorageSummaryQuery,
  useGetNodeStorageListQuery,
  useUploadNodeStorageFileMutation,
  useDeleteNodeStorageFileMutation,
  useMkdirNodeStorageFolderMutation,
  useMoveNodeStorageItemMutation,
  useGetNodeStorageSharesQuery,
  useCreateNodeStorageShareMutation,
  useUpdateNodeStorageShareMutation,
  useRotateNodeStorageShareTokenMutation,
  useRevokeNodeStorageShareMutation,
  useGetPublicNodeStorageShareSummaryQuery,
  useGetPublicNodeStorageShareListQuery,
  useGetProposalsQuery,
  useLazyGetProposalsQuery,
  useGetProposalQuery,
  useLazyGetProposalQuery,
  useGetProposalTallyQuery,
  useGetDaoEligibilityQuery,
  useLazyGetDaoEligibilityQuery,
  useCreateProposalMutation,
  useCastVoteMutation
} = indexNodeApi

/**
 * Drop every cached licence list, so the next screen that shows one refetches.
 *
 * Ownership changes where the user is standing somewhere else entirely — a
 * purchase happens on the marketplace and lands the buyer on their licence
 * page — and that page's query is already cached from before they owned
 * anything. The registry was right and the server cache was cleared; the list
 * the buyer saw came from their own browser. Dispatch this whenever a licence
 * changes hands.
 */
export const invalidateLicenceLists = () => indexNodeApi.util.invalidateTags([LicenseApiTag.LICENSE])
